/**
 * Trend series maths shared by the KPI tiles and the analytics cards.
 *
 * Lives apart from the components so `Kpi` stays presentational and both
 * JobsListScreen's TrendCard and the tiles compute a delta the same way.
 */

import type { JobsOverview } from '@cer-autofill/shared';

export type Trend = JobsOverview['analytics']['trends'][number];

/**
 * Percentage change across the trend window. The backend supplies 30 days of
 * daily counts, so the comparison is the most recent 15 days against the 15
 * before them — the honest comparison available from that series. Returns
 * undefined when the window has too little history to say anything.
 */
export function trendDelta(trend: Trend | undefined): { percent: number; up: boolean } | undefined {
  if (!trend?.sufficient) return undefined;
  const half = Math.floor(trend.points.length / 2);
  if (half === 0) return undefined;
  const sum = (points: Trend['points']) => points.reduce((total, point) => total + point.count, 0);
  const previous = sum(trend.points.slice(0, half));
  const recent = sum(trend.points.slice(half));
  if (previous === 0) return undefined;
  const percent = Math.round(((recent - previous) / previous) * 100);
  return { percent: Math.abs(percent), up: percent >= 0 };
}

/** Share of a total, clamped to 0–100. Undefined when there is no denominator. */
export function percentage(
  value: number | undefined,
  total: number | undefined,
): number | undefined {
  if (value === undefined || !total) return undefined;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}
